/**
 * One step's card — header (grip / type label / summary / issue badge /
 * delete) plus a step-type-specific body. The per-type editor JSX lives in
 * `steps/<Type>StepBody.component.tsx`; this file owns the row chrome, the
 * store subscriptions, and the `STEP_BODIES` registry that dispatches
 * `step.type` to the matching body. `loop` is the one exception — it reuses
 * `LoopStepBody`, which needs the nested StepList plumbing (scope / expand
 * state / issues map) no other body takes, so it renders via its own branch.
 */
import React, { useCallback, useRef, useState } from "react"
import { Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faChevronDown,
  faChevronRight,
  faGripVertical,
  faTriangleExclamation,
  faXmark,
} from "@fortawesome/free-solid-svg-icons"
import {
  QC_STEP_REORDER_TYPE,
  StepIssue,
  TransformStep,
  TransformStepType,
  useQueryBuilderStore,
} from "stores/queryBuilderStore"
import LoopStepBody from "./LoopStepBody.component"
import { stepSummary } from "./stepSummary"
import {
  STEP_TYPE_HINTS,
  STEP_TYPE_ICONS,
  STEP_TYPE_LABELS,
} from "./stepTypeConfig"
import StepHelpPopover from "./StepHelpPopover.component"
import { useCodebook } from "./CodebookContext"
import { StepBodyProps } from "./steps/stepBodyProps"
import CombineOrStepBody, {
  CombineOrStepBodyProps,
} from "./steps/CombineOrStepBody.component"
import AndCombineStepBody, {
  AndCombineStepBodyProps,
} from "./steps/AndCombineStepBody.component"
import NegateStepBody, {
  NegateStepBodyProps,
} from "./steps/NegateStepBody.component"
import CleanupStepBody, {
  CleanupStepBodyProps,
} from "./steps/CleanupStepBody.component"
import QTableStepBody, {
  QTableStepBodyProps,
} from "./steps/QTableStepBody.component"
import FieldDeclStepBody, {
  FieldDeclStepBodyProps,
} from "./steps/FieldDeclStepBody.component"
import AppendStepBody, {
  AppendStepBodyProps,
} from "./steps/AppendStepBody.component"
import RemoveStepBody, {
  RemoveStepBodyProps,
} from "./steps/RemoveStepBody.component"
import ConditionalAssignStepBody, {
  ConditionalAssignStepBodyProps,
} from "./steps/ConditionalAssignStepBody.component"
import CopyStructureStepBody, {
  CopyStructureStepBodyProps,
} from "./steps/CopyStructureStepBody.component"
import DefineStepBody, {
  DefineStepBodyProps,
} from "./steps/DefineStepBody.component"
import AppendStructStepBody, {
  AppendStructStepBodyProps,
} from "./steps/AppendStructStepBody.component"
import CalcStepBody, {
  CalcStepBodyProps,
} from "./steps/CalcStepBody.component"
import CatStepBody, {
  CatStepBodyProps,
} from "./steps/CatStepBody.component"
import WeightingStepBody, {
  WeightingStepBodyProps,
} from "./steps/WeightingStepBody.component"

/** Compile-checked pairing of each step type with its body's props. */
interface StepBodyPropsByType {
  combine_or: CombineOrStepBodyProps
  and_combine: AndCombineStepBodyProps
  negate: NegateStepBodyProps
  cleanup: CleanupStepBodyProps
  qtable: QTableStepBodyProps
  field_decl: FieldDeclStepBodyProps
  append: AppendStepBodyProps
  remove: RemoveStepBodyProps
  conditional_assign: ConditionalAssignStepBodyProps
  copy_structure: CopyStructureStepBodyProps
  define: DefineStepBodyProps
  appendstruct: AppendStructStepBodyProps
  calc: CalcStepBodyProps
  cat: CatStepBodyProps
  weighting: WeightingStepBodyProps
}

/**
 * step.type → body-editor registry (a partial record over TransformStepType:
 * `loop` is intentionally absent, see the file header). The mapped type
 * guarantees each key is paired with the component that edits that step type.
 */
const STEP_BODIES: {
  [T in keyof StepBodyPropsByType]: React.FC<StepBodyPropsByType[T]>
} = {
  combine_or: CombineOrStepBody,
  and_combine: AndCombineStepBody,
  negate: NegateStepBody,
  cleanup: CleanupStepBody,
  qtable: QTableStepBody,
  field_decl: FieldDeclStepBody,
  append: AppendStepBody,
  remove: RemoveStepBody,
  conditional_assign: ConditionalAssignStepBody,
  copy_structure: CopyStructureStepBody,
  define: DefineStepBody,
  appendstruct: AppendStructStepBody,
  calc: CalcStepBody,
  cat: CatStepBody,
  weighting: WeightingStepBody,
}

/**
 * Intersection of every registered body's non-`step` props — the full set of
 * callbacks / lookup data StepRow hands to whichever body is dispatched.
 * Typing the bag this way keeps the single dispatch call compile-checked:
 * add a prop to any body and this errors until StepRow supplies it.
 */
type StepBodyExtraProps = Omit<
  CombineOrStepBodyProps &
    AndCombineStepBodyProps &
    NegateStepBodyProps &
    CleanupStepBodyProps &
    QTableStepBodyProps &
    FieldDeclStepBodyProps &
    AppendStepBodyProps &
    RemoveStepBodyProps &
    ConditionalAssignStepBodyProps &
    CopyStructureStepBodyProps &
    DefineStepBodyProps &
    AppendStructStepBodyProps &
    CalcStepBodyProps &
    CatStepBodyProps &
    WeightingStepBodyProps,
  "step"
>

interface StepRowProps {
  step: TransformStep
  index: number
  /** Whether the steps list is in compact mode. */
  compact: boolean
  /** Whether THIS step is expanded (override compact for editing). */
  expanded: boolean
  /** Toggle this step's expanded override. */
  onToggleExpanded: () => void
  /** Loop variables in scope from enclosing loops. Empty at root. Children
   *  of this step's loop body are rendered with `[...scope, step.variable]`. */
  scope: string[]
  /** Per-step expand state map (passed through so nested rows share toggle state). */
  expandedIds: Set<string>
  toggleExpanded: (id: string) => void
  /** Validation issues for this step from `collectStepIssues`. */
  issues?: StepIssue[]
  /** Map of all issues, plumbed down so nested StepLists can look up
   *  their own steps' entries. */
  allIssues: Map<string, StepIssue[]>
}

const StepRow: React.FC<StepRowProps> = ({
  step,
  index,
  compact,
  expanded,
  onToggleExpanded,
  scope,
  expandedIds,
  toggleExpanded,
  issues,
  allIssues,
}) => {
  const renameStep = useQueryBuilderStore((s) => s.renameTransformStep)
  const removeStep = useQueryBuilderStore((s) => s.removeTransformStep)
  const addStepSource = useQueryBuilderStore((s) => s.addStepSource)
  const removeStepSource = useQueryBuilderStore((s) => s.removeStepSource)
  const setSingle = useQueryBuilderStore((s) => s.setStepSingleSource)
  const setCleanupMode = useQueryBuilderStore((s) => s.setCleanupMode)
  const setAppendParent = useQueryBuilderStore((s) => s.setAppendParent)
  const setCondMode = useQueryBuilderStore((s) => s.setConditionalValueMode)
  const setCondField = useQueryBuilderStore((s) => s.setConditionalValueField)
  const addCond = useQueryBuilderStore((s) => s.addConditionalCondition)
  const removeCond = useQueryBuilderStore((s) => s.removeConditionalCondition)
  const setCopySource = useQueryBuilderStore((s) => s.setCopyStructureSource)
  const setMode = useQueryBuilderStore((s) => s.setStepMode)
  const setLoopVariable = useQueryBuilderStore((s) => s.setLoopVariable)
  const setLoopIterationKind = useQueryBuilderStore(
    (s) => s.setLoopIterationKind
  )
  const setLoopFieldSource = useQueryBuilderStore((s) => s.setLoopFieldSource)
  const addLoopExcludedValue = useQueryBuilderStore(
    (s) => s.addLoopExcludedValue
  )
  const removeLoopExcludedValue = useQueryBuilderStore(
    (s) => s.removeLoopExcludedValue
  )
  const addLoopLiteralValue = useQueryBuilderStore(
    (s) => s.addLoopLiteralValue
  )
  const removeLoopLiteralValue = useQueryBuilderStore(
    (s) => s.removeLoopLiteralValue
  )
  const setLoopLiteralValues = useQueryBuilderStore(
    (s) => s.setLoopLiteralValues
  )
  const setDefineValue = useQueryBuilderStore((s) => s.setDefineValue)
  const setFieldDeclType = useQueryBuilderStore((s) => s.setFieldDeclType)
  const setFieldDeclInitialValue = useQueryBuilderStore(
    (s) => s.setFieldDeclInitialValue
  )
  const setAppendStructSource = useQueryBuilderStore(
    (s) => s.setAppendStructSource
  )
  const setAppendStructAlias = useQueryBuilderStore(
    (s) => s.setAppendStructAlias
  )
  const setCalcFieldType = useQueryBuilderStore((s) => s.setCalcFieldType)
  const setCalcExpression = useQueryBuilderStore((s) => s.setCalcExpression)
  const setCatSource = useQueryBuilderStore((s) => s.setCatSource)
  const setCatCategories = useQueryBuilderStore((s) => s.setCatCategories)
  const setCatCategoryRange = useQueryBuilderStore(
    (s) => s.setCatCategoryRange
  )
  const setWeightingMode = useQueryBuilderStore((s) => s.setWeightingMode)
  const setWeightingField = useQueryBuilderStore((s) => s.setWeightingField)
  const setWeightingDest = useQueryBuilderStore((s) => s.setWeightingDest)
  const addWeightingDimension = useQueryBuilderStore(
    (s) => s.addWeightingDimension
  )
  const removeWeightingDimension = useQueryBuilderStore(
    (s) => s.removeWeightingDimension
  )
  const setWeightingDimensionContainer = useQueryBuilderStore(
    (s) => s.setWeightingDimensionContainer
  )
  const setWeightingDimensionTargets = useQueryBuilderStore(
    (s) => s.setWeightingDimensionTargets
  )
  const setWeightingDimensionTargetValue = useQueryBuilderStore(
    (s) => s.setWeightingDimensionTargetValue
  )
  const setWeightingFilter = useQueryBuilderStore((s) => s.setWeightingFilter)
  const setWeightingNormalize = useQueryBuilderStore(
    (s) => s.setWeightingNormalize
  )
  const setWeightingNormalizeValue = useQueryBuilderStore(
    (s) => s.setWeightingNormalizeValue
  )
  const codebook = useCodebook()

  const handleAddSource = useCallback(
    (variable: string) => addStepSource(step.id, variable),
    [addStepSource, step.id]
  )
  const handleRemoveSource = useCallback(
    (variable: string) => removeStepSource(step.id, variable),
    [removeStepSource, step.id]
  )
  const cardRef = useRef<HTMLDivElement>(null)
  const [draggable, setDraggable] = useState(false)

  const handleGripMouseDown = () => setDraggable(true)
  const handleGripMouseUp = () => setDraggable(false)

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (!draggable) {
      e.preventDefault()
      return
    }
    e.dataTransfer.setData(QC_STEP_REORDER_TYPE, step.id)
    e.dataTransfer.effectAllowed = "move"
    if (cardRef.current) {
      e.dataTransfer.setDragImage(cardRef.current, 12, 18)
    }
  }
  const handleDragEnd = () => setDraggable(false)

  const showBody = !compact || expanded
  const summary = compact ? stepSummary(step) : null

  const bodyExtraProps: StepBodyExtraProps = {
    renameStep,
    setMode,
    setSingle,
    setCleanupMode,
    setFieldDeclType,
    setFieldDeclInitialValue,
    setAppendParent,
    setCondMode,
    setCondField,
    addCond,
    removeCond,
    setCopySource,
    setDefineValue,
    setAppendStructSource,
    setAppendStructAlias,
    setCalcFieldType,
    setCalcExpression,
    setCatSource,
    setCatCategories,
    setCatCategoryRange,
    setWeightingMode,
    setWeightingField,
    setWeightingDest,
    addWeightingDimension,
    removeWeightingDimension,
    setWeightingDimensionContainer,
    setWeightingDimensionTargets,
    setWeightingDimensionTargetValue,
    setWeightingFilter,
    setWeightingNormalize,
    setWeightingNormalizeValue,
    codebook,
    onAddSource: handleAddSource,
    onRemoveSource: handleRemoveSource,
  }

  // TypeScript can't correlate `step.type` with the matching entry of a
  // heterogeneous record (the classic discriminated-lookup limitation), so
  // this one cast widens the registry to the common call signature. The
  // type↔component pairing itself is checked where STEP_BODIES is declared.
  const StepBody = (
    STEP_BODIES as Partial<
      Record<TransformStepType, React.FC<StepBodyProps & StepBodyExtraProps>>
    >
  )[step.type]

  return (
    <div
      className="qc-step-row"
      id={`qc-step-${step.id}`}
      data-step-id={step.id}
    >
      <div className="qc-step-row__rail">
        <span className="qc-step-row__marker" aria-hidden="true">
          {index + 1}
        </span>
      </div>
      <div
        ref={cardRef}
        className="qc-step"
        data-step-type={step.type}
        data-compact={compact && !expanded ? "true" : "false"}
        draggable={draggable}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="qc-step__header">
          <span className="qc-step__header-left">
            <Tooltip title="Drag to reorder" placement="top">
              <span
                className="qc-step__grip"
                role="button"
                aria-label="Drag to reorder step"
                onMouseDown={handleGripMouseDown}
                onMouseUp={handleGripMouseUp}
                onMouseLeave={handleGripMouseUp}
              >
                <FontAwesomeIcon icon={faGripVertical} />
              </span>
            </Tooltip>
            {compact && (
              <button
                type="button"
                className="qc-step__expand"
                aria-label={expanded ? "Collapse step" : "Expand step"}
                aria-expanded={expanded}
                onClick={onToggleExpanded}
              >
                <FontAwesomeIcon
                  icon={expanded ? faChevronDown : faChevronRight}
                />
              </button>
            )}
            <Tooltip
              title={STEP_TYPE_HINTS[step.type]}
              placement="top"
              overlayClassName="dataq-tooltip"
            >
              <span className="qc-step__type-label">
                <FontAwesomeIcon
                  icon={STEP_TYPE_ICONS[step.type]}
                  className="qc-step__type-icon"
                />
                {STEP_TYPE_LABELS[step.type]}
              </span>
            </Tooltip>
            <StepHelpPopover type={step.type} />
            {summary && (
              <span className="qc-step__summary">
                {summary.map((tok, i) =>
                  tok.kind === "mono" ? (
                    <span key={i} className="qc-mono qc-step__summary-mono">
                      {tok.value}
                    </span>
                  ) : (
                    <span key={i}>{tok.value}</span>
                  )
                )}
              </span>
            )}
            {issues && issues.length > 0 && (
              <Tooltip
                title={
                  <ul className="qc-step__issue-list">
                    {issues.map((iss, i) => (
                      <li key={i}>{iss.message}</li>
                    ))}
                  </ul>
                }
                placement="top"
                overlayClassName="dataq-tooltip"
              >
                <span
                  className="qc-step__issue-badge"
                  data-severity={
                    issues.some((i) => i.severity === "error")
                      ? "error"
                      : "warn"
                  }
                  aria-label={`${issues.length} validation issue${
                    issues.length === 1 ? "" : "s"
                  }`}
                >
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                </span>
              </Tooltip>
            )}
          </span>
          <Tooltip title="Delete step" placement="top">
            <button
              type="button"
              className="qc-recodes__remove"
              aria-label="Remove step"
              onClick={() => removeStep(step.id)}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </Tooltip>
        </div>

        {showBody && (
          <>
            {StepBody && <StepBody step={step} {...bodyExtraProps} />}

            {step.type === "loop" && (
              <LoopStepBody
                step={step}
                scope={scope}
                compact={compact}
                expandedIds={expandedIds}
                toggleExpanded={toggleExpanded}
                allIssues={allIssues}
                onChangeVariable={(v) => setLoopVariable(step.id, v)}
                onChangeKind={(k) => setLoopIterationKind(step.id, k)}
                onChangeFieldSource={(v) => setLoopFieldSource(step.id, v)}
                onAddExcludedValue={(v) => addLoopExcludedValue(step.id, v)}
                onRemoveExcludedValue={(v) =>
                  removeLoopExcludedValue(step.id, v)
                }
                onAddLiteralValue={(v) => addLoopLiteralValue(step.id, v)}
                onRemoveLiteralValue={(v) => removeLoopLiteralValue(step.id, v)}
                onSetLiteralValues={(vals) =>
                  setLoopLiteralValues(step.id, vals)
                }
              />
            )}

            {issues && issues.length > 0 && (
              <ul className="qc-step__issues">
                {issues.map((iss, i) => (
                  <li key={i} data-severity={iss.severity}>
                    <FontAwesomeIcon
                      icon={faTriangleExclamation}
                      className="qc-step__issues-icon"
                    />
                    {iss.message}
                  </li>
                ))}
              </ul>
            )}
            <div className="qc-step__hint">{STEP_TYPE_HINTS[step.type]}</div>
          </>
        )}
      </div>
    </div>
  )
}

export default StepRow
